/**
 * The **client effect registry**. A directive that the GM fires arrives as a generic
 * envelope (`Directive { type, payload? }`, defined in `shared/`); this registry maps a
 * directive `type` → a **handler** that runs the effect on the client. Adding a new
 * effect (`shake`, `impact`, a sound cue, …) is a **one-line registration** here with
 * **no protocol change** — the wire only ever carries the generic envelope.
 *
 * Two delivery paths feed the registry, and a handler declares which it expects via
 * its `kind`:
 *
 *  - **`"standing"`** — durable view-state effects (`banner`). They are *derived* from
 *    a slot's persisted `viewState` (re-sent on every reconnect via
 *    `game_welcome.viewState`, and live via `game_view`). The registry turns a
 *    `viewState` into the set of standing effects to apply, so a reconnecting client
 *    gets its banner back.
 *
 *  - **`"ephemeral"`** — fire-and-forget effects (`glitch`, a one-shot toast). They
 *    arrive on `game_fx_event` and run exactly **once**; they are never persisted or
 *    replayed, so a client that reconnects after one fired never sees it.
 *
 * The registry itself is framework-agnostic: handlers receive an {@link EffectContext}
 * of imperative capabilities (set/clear the banner, fire a transient FX) so
 * the registry never imports React. The hook (`useGameEffects`) supplies the context.
 */
import type { Directive, DirectiveBanner } from "@lancer-vtt/shared";

/** A standing banner an effect may pin (text, optional image, optional tone). */
export type BannerSpec = DirectiveBanner;

/**
 * The imperative capabilities a directive handler may use. Supplied by the hook so
 * the registry stays decoupled from React/router specifics. A handler uses only what
 * its effect needs.
 */
export type EffectContext = {
  /** Pin (or, with null, clear) the standing in-fiction banner. */
  setBanner(banner: BannerSpec | null): void;
  /** Fire a transient, one-shot visual FX keyed by name (e.g. "glitch"). */
  fireTransient(effect: string, payload?: unknown): void;
}

/**
 * A directive handler. `kind` selects the delivery channel:
 *  - `"standing"` handlers are run with a directive *derived from* the slot's
 *    persisted view-state (so they re-apply on reconnect).
 *  - `"ephemeral"` handlers are run once, on a `game_fx_event`.
 */
export type EffectHandler = {
  kind: "standing" | "ephemeral";
  run(payload: unknown, ctx: EffectContext): void;
}

/** The registry: directive `type` → handler. Keyed by type — the extensibility seam. */
export type EffectRegistry = Map<string, EffectHandler>;

// ── The default effects ──────────────────────────────────────────────────────
//
// THE EXTENSIBILITY SEAM: a new effect is one `register(...)` call below. The wire
// protocol does not change — the generic `Directive` envelope already carries it.

/** Register an effect under `type`. Adding an effect is exactly this one line. */
export function register(registry: EffectRegistry, type: string, handler: EffectHandler): void {
  registry.set(type, handler);
}

/**
 * Build the default registry: `banner` (standing) and `glitch` / `impact` / `shake`
 * (ephemeral). New effects slot in with another `register(...)` — no protocol churn.
 */
export function defaultEffectRegistry(): EffectRegistry {
  const registry: EffectRegistry = new Map();

  // banner (standing): pin payload.banner (text|text+image, with its tone), or clear it when null.
  register(registry, "banner", {
    kind: "standing",
    run: (payload, ctx) => {
      const banner = (payload as { banner?: BannerSpec | null } | undefined)?.banner ?? null;
      ctx.setBanner(banner);
    },
  });

  // glitch (ephemeral): a one-shot visual FX — fire-and-forget, never replayed.
  register(registry, "glitch", {
    kind: "ephemeral",
    run: (payload, ctx) => ctx.fireTransient("glitch", payload),
  });

  // impact (ephemeral): a one-shot hot-white bloom and punch — fire-and-forget, never replayed.
  const impact: EffectHandler = { kind: "ephemeral", run: (payload, ctx) => ctx.fireTransient("impact", payload) };
  register(registry, "impact", impact);
  // flash: impact's old name, still accepted so an older client's directive or a logged one plays.
  register(registry, "flash", impact);

  // shake (ephemeral): a one-shot screen-shake — fire-and-forget, never replayed.
  register(registry, "shake", { kind: "ephemeral", run: (payload, ctx) => ctx.fireTransient("shake", payload) });

  // full-repair (ephemeral): the FULL REPAIR sweep banner, payload `{ sub?: string }` (who was repaired).
  register(registry, "full-repair", { kind: "ephemeral", run: (payload, ctx) => ctx.fireTransient("full-repair", payload) });

  return registry;
}

/**
 * Turn a slot's standing `viewState` into the set of **standing directives** to apply —
 * the bridge between the durable `SlotViewState` and the generic registry.
 * `activeBanner` → a `banner` directive, always emitted so clearing it to null applies
 * too. This is what a reconnecting client runs off `game_welcome.viewState`.
 */
export function directivesForViewState(view: { activeBanner: BannerSpec | null }): Directive[] {
  return [{ type: "banner", payload: { banner: view.activeBanner } }];
}

/**
 * Run a directive through the registry against `ctx`. Unknown types are ignored
 * (forward-compatible: an old client silently drops an effect it doesn't know).
 * `expectedKind`, when given, also guards the channel (a `game_fx_event` carrying a
 * `"standing"` type, or vice-versa, is dropped).
 */
export type RunDirectiveArgs = {
  registry: EffectRegistry;
  directive: Directive;
  ctx: EffectContext;
  expectedKind?: EffectHandler["kind"];
};

export function runDirective(args: RunDirectiveArgs): void {
  const { registry, directive, ctx, expectedKind } = args;
  const handler = registry.get(directive.type);
  if (handler === undefined) return;
  if (expectedKind !== undefined && handler.kind !== expectedKind) return;
  handler.run(directive.payload, ctx);
}
